import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { Editor } from "@tiptap/react";

interface LinkModalProps {
  show: boolean;
  onHide: () => void;
  editor: Editor;
}

const LinkModal: React.FC<LinkModalProps> = ({ show, onHide, editor }) => {
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [hasExistingLink, setHasExistingLink] = useState(false);
  const urlInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (show && editor) {
      // Check if current selection has a link
      const existingUrl = editor.getAttributes("link").href || "";
      setHasExistingLink(!!existingUrl);
      setUrl(existingUrl);

      // Get selected text
      const { from, to } = editor.state.selection;
      if (from !== to) {
        setText(editor.state.doc.textBetween(from, to, " "));
      } else {
        setText("");
      }

      // Focus URL input after modal opens
      setTimeout(() => urlInputRef.current?.focus(), 200);
    }
  }, [show, editor]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return;

    // Ensure URL has protocol and block dangerous schemes
    let finalUrl = url.trim();
    const dangerousProtocols = ["javascript:", "data:", "vbscript:"];
    if (dangerousProtocols.some(p => finalUrl.toLowerCase().startsWith(p))) {
      return; // Block XSS vectors
    }
    if (!/^https?:\/\//i.test(finalUrl) && !/^mailto:/i.test(finalUrl)) {
      finalUrl = `https://${finalUrl}`;
    }

    const { from, to } = editor.state.selection;

    if (from === to && text.trim()) {
      // No selection but user typed display text - insert new text with link
      // Use structured content to avoid HTML injection from user input
      editor
        .chain()
        .focus()
        .insertContent({
          type: "text",
          text: text,
          marks: [{ type: "link", attrs: { href: finalUrl } }],
        })
        .run();
    } else {
      // Apply link to selection
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: finalUrl })
        .run();
    }

    onHide();
  };

  const handleRemoveLink = () => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    onHide();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="sm">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "16px" }}>
          {hasExistingLink ? "Edit Link" : "Insert Link"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: "13px", fontWeight: 500 }}>
              URL
            </Form.Label>
            <Form.Control
              ref={urlInputRef}
              type="text"
              placeholder="https://example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={handleKeyDown}
              size="sm"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: "13px", fontWeight: 500 }}>
              Display text (optional)
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Link text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              size="sm"
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer style={{ padding: "8px 16px" }}>
        {hasExistingLink && (
          <Button
            variant="outline-danger"
            size="sm"
            onClick={handleRemoveLink}
            style={{ marginRight: "auto" }}
          >
            Remove Link
          </Button>
        )}
        <Button variant="outline-secondary" size="sm" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={!url.trim()}
        >
          {hasExistingLink ? "Update" : "Insert"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LinkModal;
